/**
 * ElectronicModulePicker — Algolia-backed typeahead for linking a WatchReference
 * to an `electronicModules/{id}` doc. Mirrors the calibre-family BaseCalibrePicker
 * typeahead (debounced search → dropdown → chip), but searches the whole module
 * index (a digital ref can carry any maker's module) and persists the selected
 * module's doc id via `onChange`. The Cloud Function fan-out trigger populates
 * the ref's `electronics` cache from `moduleId` — this picker only writes the id.
 */
import { useEffect, useRef, useState } from 'react';
import {
  searchElectronicModules,
  type ElectronicModuleHit,
} from '../../services/algolia';
import {
  getElectronicModuleById,
  type SuggestElectronicModuleInput,
} from '../../services/firebase';
import { ElectronicModuleEditorModal } from './ElectronicModuleEditorModal';
import { ElectronicModuleAiLookup } from './ElectronicModuleAiLookup';

interface ElectronicModulePickerProps {
  /** Currently linked module doc id (empty string when unlinked). */
  value: string;
  /** Known display label for `value`, if the caller already has one (e.g. the
   *  cached `electronics.module` name) — avoids a fetch on open. */
  initialLabel?: string;
  /** Persist the selected module id (empty string clears the link). */
  onChange: (moduleId: string, label: string) => void;
  /** Pre-fill the manufacturer when creating a new module (resolved from the
   *  ref's brand manufacturer, when available). */
  defaultManufacturerId?: string;
  /** Seed for the "Create New" modal name (e.g. a legacy `elec.module` value). */
  createNameSeed?: string;
  /** Build the `suggestElectronicModule` input at click time (brand + product +
   *  legacy values). When provided, the "AI Lookup" button is enabled. */
  aiContext?: () => SuggestElectronicModuleInput;
  /** When provided, a refresh button next to the linked-module chip re-pulls the
   *  module doc (so an edit made on the module editor shows without navigating). */
  onRefresh?: () => void;
  /** Spinner state for the refresh button. */
  refreshing?: boolean;
  disabled?: boolean;
}

export function ElectronicModulePicker({
  value,
  initialLabel,
  onChange,
  defaultManufacturerId,
  createNameSeed,
  aiContext,
  onRefresh,
  refreshing = false,
  disabled = false,
}: ElectronicModulePickerProps) {
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<ElectronicModuleHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(initialLabel || '');
  const [createOpen, setCreateOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve the linked module's name for the chip when we weren't handed one.
  useEffect(() => {
    let cancelled = false;
    if (value && !label) {
      getElectronicModuleById(value)
        .then((m) => {
          if (!cancelled && m) setLabel(m.name);
        })
        .catch(() => {});
    }
    if (!value) setLabel('');
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchElectronicModules(q)
        .then((res) => {
          setHits(res);
          setOpen(true);
        })
        .catch((err) => {
          console.error('Module search failed', err);
          setHits([]);
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const select = (hit: ElectronicModuleHit) => {
    setLabel(hit.name);
    onChange(hit.objectID, hit.name);
    setSearch('');
    setHits([]);
    setOpen(false);
  };

  const clear = () => {
    setLabel('');
    onChange('', '');
  };

  const handleCreated = (savedId: string) => {
    // Link the new module immediately, but keep the editor open (it self-manages
    // closing) so it can be AI-enriched in place before the user dismisses it.
    // Label resolves via the value effect (getElectronicModuleById) once linked.
    setLabel('');
    onChange(savedId, '');
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Module{' '}
        <span className="font-normal text-gray-400">(linked entity)</span>
      </label>
      {value ? (
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 bg-brand-50 border border-brand-200 text-brand-800 text-sm rounded-full pl-3 pr-1.5 py-1">
            <span className="font-medium">{label || value}</span>
            <button
              type="button"
              onClick={clear}
              disabled={disabled}
              aria-label="Unlink module"
              className="rounded-full hover:bg-brand-100 w-5 h-5 inline-flex items-center justify-center text-brand-600"
            >
              ×
            </button>
          </span>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={disabled || refreshing}
              aria-label="Refresh module details"
              title="Refresh module details"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            >
              <svg
                className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          )}
        </div>
      ) : null}

      <div className="relative">
        <input
          type="text"
          value={search}
          disabled={disabled}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => hits.length > 0 && setOpen(true)}
          placeholder="Search modules (e.g. 3159, UTD-8000)…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        {searching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            …
          </span>
        )}
        {open && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {hits.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">
                No matching modules.
              </li>
            ) : (
              hits.map((h) => (
                <li key={h.objectID}>
                  <button
                    type="button"
                    onClick={() => select(h)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <span className="text-sm text-gray-800 flex-1">
                      {h.name}
                    </span>
                    {(h.displayType || h.battery) && (
                      <span className="text-xs text-gray-400">
                        {[h.displayType, h.battery]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        {/* Creating a new module is redundant once one is linked — unlink first
            to switch. AI Lookup stays available for searching a different one. */}
        {!value && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={disabled}
            className="text-xs font-medium px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            + Create New
          </button>
        )}
        {aiContext && (
          <ElectronicModuleAiLookup
            buildInput={aiContext}
            onLink={(id, name) => {
              setLabel(name);
              onChange(id, name);
            }}
            defaultManufacturerId={defaultManufacturerId}
            variant="full"
            disabled={disabled}
          />
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Links this reference to a shared module entity. The electronics fields
        below are an auto-synced cache once a module is linked.
      </p>

      <ElectronicModuleEditorModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        module={null}
        defaultManufacturerId={defaultManufacturerId}
        defaultName={createNameSeed}
        onSaved={handleCreated}
      />
    </div>
  );
}
